/* Micro drama converter API (Backend: /api/v1/micro-drama, docs/MICRO_DRAMA.md).
   Turns a long video into short vertical episodes of a Micro Drama season. */

import { get, post } from "../../../api/methods";
import { seriesListApi } from "../../../api/HttpApis";

const unwrap = (res) => res?.data?.data;

export const MICRO_DRAMA_FORMAT = "Micro Drama";
export const isMicroDramaFormat = (format) =>
  String(format || "").trim().toLowerCase() === MICRO_DRAMA_FORMAT.toLowerCase();

/** Episode-length choices (seconds). */
export const LENGTH_OPTIONS = [
  { value: 60, label: "1 min" },
  { value: 90, label: "1.5 min" },
  { value: 120, label: "2 min" },
  { value: 180, label: "3 min" },
  { value: 300, label: "5 min" },
];
export const DEFAULT_LENGTH = 120;

export const IN_FLIGHT = ["pending", "processing"];

/** Error text from an API error. */
export function apiErrorMessage(err, fallback = "Something went wrong") {
  return err?.response?.data?.message || err?.message || fallback;
}

export const describeSource = async (videoId, targetSeconds = DEFAULT_LENGTH) =>
  unwrap(await get(`/micro-drama/sources/${videoId}?target_seconds=${targetSeconds}`));

export const listConversions = async (seriesId, seasonId) =>
  unwrap(await get(`/micro-drama/conversions?series_id=${seriesId}${seasonId ? `&season_id=${seasonId}` : ""}`)) || [];

export const startConversion = async (body) => unwrap(await post("/micro-drama/conversions", body));
export const startSeriesConversion = async (body) => unwrap(await post("/micro-drama/conversions/batch", body));
export const describeSeriesSource = async (seriesId, targetSeconds = DEFAULT_LENGTH) =>
  unwrap(await get(`/micro-drama/series-sources/${seriesId}?target_seconds=${targetSeconds}`));
export const conversionEpisodes = async (id) => unwrap(await get(`/micro-drama/conversions/${id}/episodes`)) || [];
export const publishConversion = async (id) => (await post(`/micro-drama/conversions/${id}/publish`, {}))?.data;

/** Series to pick from for "a whole series" (the CMS series list, searched by name). */
export const searchSeries = async (search = "") => {
  const res = await seriesListApi({}, "", "", "", "", 20, 1, search);
  const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
  return rows.map((r) => ({ id: r.id, name: r.name, image: r.portrait || null, format: r.series_format || "" }));
};
export const cancelConversion = async (id) => unwrap(await post(`/micro-drama/conversions/${id}/cancel`, {}));
export const retryConversion = async (id) => unwrap(await post(`/micro-drama/conversions/${id}/retry`, {}));

/** "1h 52m", "14m 05s", "48s" */
export function formatLength(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m) return `${m}m ${String(r).padStart(2, "0")}s`;
  return `${r}s`;
}

/** Rough episode count (the server snaps cuts to scene changes, so ± a few). */
export function estimateEpisodes(durationSec, targetSec) {
  const d = Number(durationSec);
  const t = Number(targetSec);
  if (!(d > 0) || !(t > 0)) return 0;
  return Math.max(1, Math.round(d / t));
}

/** One line describing where a conversion is. */
export function conversionStatusText(c) {
  if (!c) return "";
  const total = c.parts_total || 0;
  const done = c.parts_done || 0;
  const eps = c.episodes || [];
  const range = eps.length
    ? ` (Ep ${Math.min(...eps.map((e) => e.episode_number))}–${Math.max(...eps.map((e) => e.episode_number))})`
    : "";
  switch (c.status) {
    case "pending":
      return "Queued — starts when the converter is free";
    case "processing":
      if (c.stage === "analyzing" || !total) return "Finding scene changes…";
      return `${c.stage === "uploading" ? "Uploading" : "Making"} episode ${Math.min(done + 1, total)} of ${total}`;
    case "ready":
      return `Done — ${done} episode${done === 1 ? "" : "s"} added${range}`;
    case "canceled":
      return done ? `Stopped after ${done} of ${total} episodes${range}` : "Stopped";
    case "failed":
      return done ? `Failed after ${done} of ${total} episodes` : "Failed";
    default:
      return c.status;
  }
}

/** "0:00–1:58" */
export function formatRange(start, end) {
  const t = (x) => {
    const n = Math.max(0, Math.round(Number(x) || 0));
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const sec = String(n % 60).padStart(2, "0");
    return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
  };
  return `${t(start)}–${t(end)}`;
}

/**
 * The panel's cards: a single-video conversion is one card; a whole-series
 * conversion (rows sharing batch_id) is one card for the whole series.
 * Newest first.
 */
export function groupConversions(list) {
  const groups = [];
  const byBatch = new Map();
  for (const c of list || []) {
    if (!c.batch_id) {
      groups.push({ key: `c${c.id}`, kind: "single", id: c.id, rows: [c], ...summarizeSingle(c) });
      continue;
    }
    if (!byBatch.has(c.batch_id)) {
      const g = { key: `b${c.batch_id}`, kind: "batch", rows: [] };
      byBatch.set(c.batch_id, g);
      groups.push(g);
    }
    byBatch.get(c.batch_id).rows.push(c);
  }
  for (const g of byBatch.values()) {
    g.rows.sort((a, b) => (a.batch_index || 0) - (b.batch_index || 0));
    Object.assign(g, summarizeBatch(g.rows));
  }
  return groups;
}

function summarizeSingle(c) {
  return {
    title: c.source_title || `Video #${c.source_video_id}`,
    status: c.status,
    progress: c.progress || 0,
    text: conversionStatusText(c),
    error: c.status === "failed" ? c.error : null,
    partsDone: c.parts_done || 0,
    reframe: c.reframe,
    target: c.target_seconds,
    created: c.created_at,
    completed: c.completed_at,
  };
}

function summarizeBatch(rows) {
  const total = rows[0]?.batch_total || rows.length;
  const done = rows.filter((r) => r.status === "ready").length;
  const partsDone = rows.reduce((n, r) => n + (r.parts_done || 0), 0);
  const eps = rows.flatMap((r) => r.episodes || []);
  const range = eps.length
    ? ` (Ep ${Math.min(...eps.map((e) => e.episode_number))}–${Math.max(...eps.map((e) => e.episode_number))})`
    : "";
  const running = rows.find((r) => r.status === "processing");
  const failed = rows.find((r) => r.status === "failed");
  const canceled = rows.some((r) => r.status === "canceled");
  const pending = rows.some((r) => r.status === "pending");
  const label = (r) => `episode ${r.batch_index} of ${total}${r.source_title ? ` (${r.source_title})` : ""}`;
  let status;
  let text;
  if (running) {
    status = "processing";
    text = `Converting ${label(running)} — ${conversionStatusText(running).replace(/^./, (x) => x.toLowerCase())}`;
  } else if (failed) {
    status = "failed";
    text = `Failed on ${label(failed)} · ${done} of ${total} episodes done`;
  } else if (done === rows.length) {
    status = "ready";
    text = `Done — ${total} episodes turned into ${partsDone} micro episode${partsDone === 1 ? "" : "s"}${range}`;
  } else if (pending && !canceled) {
    status = "pending";
    text = done ? `${done} of ${total} episodes done — next one starting` : `Queued — ${total} episodes, converted one after another`;
  } else {
    status = "canceled";
    text = `Stopped after ${done} of ${total} episodes${partsDone ? ` · ${partsDone} micro episodes made${range}` : ""}`;
  }
  const progress = Math.round(rows.reduce((n, r) => n + (r.status === "ready" ? 100 : r.progress || 0), 0) / Math.max(1, rows.length));
  const first = rows[0] || {};
  return {
    id: (running || failed || first).id,
    title: `${first.batch_title || "Series"} — whole series (${total} episodes)`,
    status,
    progress,
    text,
    error: failed ? failed.error : null,
    partsDone,
    reframe: first.reframe,
    target: first.target_seconds,
    created: first.created_at,
    completed: rows.every((r) => r.completed_at) ? rows.map((r) => r.completed_at).sort().pop() : null,
  };
}
